import React, {useEffect, useState} from "react";

import {Box, Divider} from '@mui/material';

import "../App.css"
import {PageHeader} from "./PageHeader";
import {useAppCtx} from "../app/AppCtx";
import {useCodeCtx} from "../app/CodeCtx";
import {useCrewCtx} from "../app/CrewCtx";
import {CxDataGrid, CxDataPairs} from "../cmx/CxData";
import {CxGrid} from "../cmx/CxGrid";
import {CxListFeedback} from "../cmx/CxFeedback";
import {Task} from "../domain/Task";
import {Project} from "../domain/Project"
import {runningCall, successCall} from "../gateway/CallService";
import {archiveRestoreProject} from "../gateway/ArchiveService"
import {archiveTaskList} from "../gateway/ProjectService"
import {canRestoreProject} from "../ctx/AccessCtrl";
import {CxConfirmDialog} from "../cmx/CxDialog";


// --------------------------------------------------------------------------------------------------------------------
// renderHeader
// --------------------------------------------------------------------------------------------------------------------

const RenderHeader = (projectData) => {
    // context's
    const { handleAction } = useAppCtx();
    const { crew } = useCrewCtx();

    const foremanIsInactive = ! crew.isActive(projectData.foreman_crew_id);

    // project restore with confirm
    const [openConfirm, setOpenConfirm] = useState(false);
    const [call, setCall] = useState(successCall());

    const handleLocalActionChange = (value) => {
        if (value === "projectRestore") {
            setOpenConfirm(true)
            return
        }
    };

    const handleConfirm = async (action) => {
        setOpenConfirm(false);
        if (action === "yes") {
            const projectId = projectData.id;
            const clearForeman = foremanIsInactive;
            setCall(runningCall());
            archiveRestoreProject(projectId, clearForeman).then(responseCall => {
                if (responseCall.isSuccess) {
                    setCall(successCall())
                    const restoredProject = responseCall.result.project
                    handleAction("projectDetail", restoredProject);
                } else {
                    setCall(responseCall);
                }
            });
        }
    }

    // project rendering
    const title = `Archiviertes Projekt ${projectData.project_ref}`;

    const confirmText = foremanIsInactive
        ? "Soll das Projekt mit allen Arbeitsvorgängen wieder aktiviert werden? Beachte: Der Bauführer ist inaktiv und wird bei der Wiederherstellung entfernt."
        : "Soll das Projekt mit allen Arbeitsvorgängen wieder aktiviert werden?";

    const localActionItems= [
        { kind: "icon",    label: "Wiederherstellen", iconName: "restore",   actionName: "projectRestore",
            disabled: !canRestoreProject(projectData.foreman_crew_id) }
    ]

    const actionItems = [
        { kind: "tooltip", label: "Zurück zu Projekte suchen", iconName: "searchProject", actionName: "projectSearch" }
    ];

    return (
        <>
            <PageHeader
                title={title}
                localActionItems={localActionItems}
                localActionOnChangeHandler={handleLocalActionChange}
                actionItems={actionItems} />

            <CxListFeedback call={call} />

            <CxConfirmDialog
                open={openConfirm}
                title="Projekt wiederherstellen"
                text={confirmText}
                handleConfirm={handleConfirm}
                severity="Info" />
        </>
    );
}

const RenderTasksHeader = () => {

    const title = "Arbeitsvorgänge";

    const actionItems = [
        { kind: "tooltip", label: "Zurück zu Arbeitsvorgänge suchen", iconName: "searchTask", actionName: "taskSearch" }
    ];

    return (
        <PageHeader
            title={title}
            isSubtitle={true}
            actionItems={actionItems}
        />
    );
}

// --------------------------------------------------------------------------------------------------------------------
// RenderProject
// --------------------------------------------------------------------------------------------------------------------

const RenderProject = (currentData) => {
    const {lazyloadCrews} = useCrewCtx();         // load crew for RenderProject and RenderTasks
    const {lazyloadCodes} = useCodeCtx();         // load code for RenderTasks already here

    useEffect(() => {
        lazyloadCodes();
        lazyloadCrews();
    }, []);

    function mapObjectToDisplayItems(obj, columns) {
        return columns.map(col => ({
            key: col.field,
            label: col.headerName,
            value: obj[col.field]
        }));
    }

    const columns = Project.getArchiveColumns();
    const displayItems = mapObjectToDisplayItems(currentData, columns);

    return (
        <CxDataPairs items={displayItems} size={"md"} />
    );
}

// --------------------------------------------------------------------------------------------------------------------
// RenderTasks
// --------------------------------------------------------------------------------------------------------------------

const RenderTasks = (projectData) => {
    const { code } = useCodeCtx();
    const { crew } = useCrewCtx();

    // select tasks
    const [call, setCall] = useState(runningCall());

    useEffect(() => {
        setCall(runningCall())
        archiveTaskList(projectData.id).then(responseCall => {

            if (responseCall.isSuccess) {
                const mappedRows = Task.mapListColumns(responseCall.result, code, crew, true);
                setCall(successCall(mappedRows));

            } else {
                setCall(responseCall);
            }
        });

    }, [projectData.id]);

    // show tasks
    return (
        <>
            <CxListFeedback call={call} />

            { call.isSuccess &&
                <CxGrid size="xl">
                    <CxDataGrid
                        rows={call.result}
                        columns={Task.getArchiveColumns()}
                    />
                </CxGrid>
            }
        </>
    );
}

// --------------------------------------------------------------------------------------------------------------------
// RenderDetail
// --------------------------------------------------------------------------------------------------------------------

const RenderDetail = (projectData) => {

    return (
        <Box sx={{ width: '100%' }}>
            <CxGrid container spacing={2}>
                { RenderHeader(projectData) }
                { RenderProject(projectData) }
            </CxGrid>

            <Divider sx={{ my: 2 }} />

            <CxGrid container spacing={2}>
                { RenderTasksHeader() }
                { RenderTasks(projectData) }
            </CxGrid>
        </Box>
    );
}

// --------------------------------------------------------------------------------------------------------------------
// ProjectDetail
// --------------------------------------------------------------------------------------------------------------------

export function ArchiveDetail() {
    const { currentData } = useAppCtx();        // currentData contains project data from archived project search
    return RenderDetail(currentData);
}

export function ArchiveDetailFromTask() {
    const { currentData } = useAppCtx();        // currentData contains task data from archived task search
    return RenderDetail(Task.extractProjectData(currentData));
}

// --------------------------------------------------------------------------------------------------------------------
// The end.